import {
  ChangeDetectionStrategy,
  Component,
  forwardRef,
  Input,
} from "@angular/core";
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  Validators,
} from "@angular/forms";
import { ContactDetails } from "pages/company-resources/contact-details/contact-details";
import { PermissionsResponse } from "../../../../app/access-control/permissions-response";
import { commonVar } from "../../../../providers/common-var/common";
import { ResourceManagementService } from "../../../../providers/resource-management/resource-management.service";

@Component({
  selector: "workforce-schedule-contact-details",
  templateUrl: "workforce-schedule-contact-details.html",
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => WorkforceScheduleContactDetails),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => WorkforceScheduleContactDetails),
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WorkforceScheduleContactDetails extends ContactDetails {
  timeString = ["morningTime", "afternoonTime", "eveningTime"];
  zipCodePattern = commonVar.ZIP_PATTERN;
  protected readonly String = String;
  dayOptions = [
    {
      display: "Monday",
      value: 0,
    },
    {
      display: "Tuesday",
      value: 1,
    },
    {
      display: "Wednesday",
      value: 2,
    },
    {
      display: "Thursday",
      value: 3,
    },
    {
      display: "Friday",
      value: 4,
    },
    {
      display: "Saturday",
      value: 5,
    },
    {
      display: "Sunday",
      value: 6,
    },
  ];
  dayOptionsOpen: boolean = false;

  @Input() isEditMode: boolean = false;

  _permissionsAccess = {} as PermissionsResponse;

  @Input()
  set permissionsAccess(permissionsAccess: PermissionsResponse) {
    if (
      permissionsAccess &&
      permissionsAccess.permissions &&
      permissionsAccess.permissions.length > 0
    ) {
      this._permissionsAccess = permissionsAccess;
    }
  }

  get schedules() {
    return this.innerFormGroup.get("schedules") as FormArray;
  }

  set value(value: any) {
    if (value.schedules) {
      for (let i = 0; i < value.schedules.length; i++) {
        if (value.schedules[i].radius || value.schedules[i].zip) {
          // CO-3511: this pushed a NEW schedule row on every writeValue with no
          // existing-length guard, so re-hydrating the same volunteer (which happens
          // routinely — the parent patches its form more than once per edit)
          // duplicated the schedule rows on screen and in the saved payload.
          //
          // The `event` passed to addSchedule/addDay was also neither a parameter nor
          // a local: it resolved to the deprecated global `window.event`, so if
          // hydration happened while a DOM event was in flight, preventDefault() was
          // called on the user's own click. Pass null — these are not event handlers.
          if (this.schedules.length <= i) {
            this.addSchedule(null);
          }
          if (value.schedules[i].morning) this.addDay(null, 0, i);
          if (value.schedules[i].afternoon) this.addDay(null, 1, i);
          if (value.schedules[i].evening) this.addDay(null, 2, i);
        }
      }
    }

    this.innerFormGroup.patchValue(value);
    this.onChange(value);
    this.onTouched();
  }

  constructor(
    protected formBuilder: FormBuilder,
    protected resourceManagementService: ResourceManagementService
  ) {
    super(formBuilder);

    this.innerFormGroup = this.formBuilder.group({
      schedules: new FormArray([]),
    });

    this.subscriptions.push(
      this.innerFormGroup.valueChanges.subscribe((value) => {
        this.onChange(value);
        this.onTouched();
      })
    );
  }

  valid(index: number, control: string) {
    return this.schedules.at(index).get(control).valid;
  }

  touched(index: number, control: string) {
    return this.schedules.at(index).get(control).touched;
  }

  validate(_: FormControl) {
    return this.innerFormGroup.valid ? null : { schedules: { valid: false } };
  }

  addSchedule(event) {
    if (event) event.preventDefault();
    const group = this.formBuilder.group({
      zip: [
        null,
        [Validators.required, Validators.pattern(this.zipCodePattern)],
      ],
      radius: [null, [Validators.required, Validators.pattern("^[0-9]*$")]],
      morning: [false],
      afternoon: [false],
      evening: [false],
    });

    this.schedules.push(group);
  }

  removeSchedule(event, index) {
    if (event) event.preventDefault();
    this.schedules.removeAt(index);
  }

  addDay(event, time, index) {
    if (event) event.preventDefault();
    let scheduleForm = this.schedules.at(index) as FormGroup;
    if (!scheduleForm.contains(this.timeString[time])) {
      scheduleForm.addControl(
        this.timeString[time],
        this.formBuilder.control("", Validators.required)
      );
    }
  }
  removeDay(event, time, index) {
    if (event) event.preventDefault();
    let scheduleForm = this.schedules.at(index) as FormGroup;
    if (scheduleForm.contains(this.timeString[time])) {
      scheduleForm.removeControl(this.timeString[time]);
    }
  }
}



// WEBPACK FOOTER //
// ./src/components/system-admin/workforce/workforce-schedule/workforce-schedule-contact-details.ts